/**
 * Phase 4 — Report a Near-Miss incident. Responsive (desktop + /m/near-miss).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Plus, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import OfflineReadyBadge from '../../components/OfflineReadyBadge';
import { apiPost } from '../../lib/api';
import { enqueue, cachedGet, remainingPhotoSlots } from '../../lib/offlineQueue';
import { toast } from 'sonner';

const SEVERITIES = [
  { v: 'low', label: 'Low', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  { v: 'medium', label: 'Medium', tone: 'bg-amber-50 text-amber-700 border-amber-300' },
  { v: 'high', label: 'High', tone: 'bg-orange-50 text-orange-700 border-orange-300' },
  { v: 'critical', label: 'Critical', tone: 'bg-red-50 text-red-700 border-red-300' },
];

export default function RaiseNearMiss() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: '', incident_time: '', location_on_site: '', description: '',
    potential_severity: 'high', actual_consequence: '', what_could_have_happened: '', root_cause: '',
  });
  const [photos, setPhotos] = useState([]);
  const [factors, setFactors] = useState([]);
  const [factorInput, setFactorInput] = useState('');
  const [saving, setSaving] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.location.pathname.startsWith('/m');

  useEffect(() => {
    cachedGet('projects', '/projects').then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setProjects(list);
      if (list.length) setForm((f) => ({ ...f, project_id: f.project_id || list[0].id }));
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const addFactor = () => {
    const v = factorInput.trim();
    if (v && !factors.includes(v)) setFactors([...factors, v]);
    setFactorInput('');
  };

  const submit = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSaving(true);

    const queueOffline = async () => {
      const remaining = await remainingPhotoSlots();
      if (photos.length > remaining) {
        toast.error(`Only ${remaining} offline photo slot(s) left — sync pending entries first.`);
        return false;
      }
      await enqueue('near_miss', { payload: { ...form, contributing_factors: factors }, photos });
      toast.success('Saved offline — will sync when you reconnect');
      navigate('/m/safety');
      return true;
    };

    try {
      if (isMobile && typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOffline();
        setSaving(false);
        return;
      }
      await apiPost('/safety/near-miss', { ...form, contributing_factors: factors, photos_base64: photos });
      toast.success('Near-miss reported');
      navigate('/app/safety');
    } catch (e) {
      if (isMobile && !e?.response && await queueOffline()) {
        setSaving(false);
        return;
      }
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-4 sm:py-0">
      <PageHeader overline="Safety" title="Report Near-Miss" subtitle="An incident that could have caused harm — capture it so it never becomes an accident" />

      <div className="space-y-4 bg-white sm:border sm:border-slate-200 rounded-sm sm:p-6" data-testid="raise-near-miss-form">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
          <select value={form.project_id} onChange={set('project_id')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="nm-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Incident time</label>
            <input type="datetime-local" value={form.incident_time} onChange={set('incident_time')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="nm-incident-time" />
          </div>
          <div>
            <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Location on site</label>
            <input value={form.location_on_site} onChange={set('location_on_site')} placeholder="e.g. Tower crane radius, Gate 2" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="nm-location" />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Potential severity</label>
          <div className="grid grid-cols-4 gap-2">
            {SEVERITIES.map((s) => (
              <button key={s.v} type="button" onClick={() => setForm({ ...form, potential_severity: s.v })}
                className={`px-2 py-2.5 rounded-sm text-xs font-bold border transition-all ${form.potential_severity === s.v ? `${s.tone} ring-2 ring-offset-1 ring-current` : 'bg-white text-slate-500 border-slate-300'}`}
                data-testid={`nm-severity-${s.v}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">What happened?</label>
          <textarea value={form.description} onChange={set('description')} rows={3} placeholder="Describe the near-miss" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="nm-description" />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">What could have happened?</label>
          <textarea value={form.what_could_have_happened} onChange={set('what_could_have_happened')} rows={2} placeholder="The worst credible outcome" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="nm-could-have" />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Actual consequence</label>
          <input value={form.actual_consequence} onChange={set('actual_consequence')} placeholder="e.g. None / no injury" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="nm-actual" />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Contributing factors</label>
          <div className="flex gap-2 mb-2">
            <input value={factorInput} onChange={(e) => setFactorInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFactor(); } }} placeholder="e.g. poor lighting" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="nm-factor-input" />
            <button type="button" onClick={addFactor} className="px-3 py-2 bg-slate-100 rounded-sm hover:bg-slate-200" data-testid="nm-factor-add"><Plus size={16} /></button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {factors.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-sm">
                {f}<button type="button" onClick={() => setFactors(factors.filter((_, j) => j !== i))}><X size={11} /></button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Photos</label>
          <PhotoCaptureField photos={photos} setPhotos={setPhotos} />
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-3.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium"
          data-testid="nm-submit-btn">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
          {saving ? 'Submitting…' : 'Report Near-Miss'}
        </button>
        {isMobile && <div className="flex justify-center pt-1"><OfflineReadyBadge /></div>}
      </div>
    </div>
  );
}
